import { useCallback, useEffect, useState } from 'react';
import { REFERRAL_CODE_KEY } from '../constants/referral.const';

export const useReferralCode = () => {
  const [refCode, setRefCode] = useState<string | null>(null);

  // Get referral code from localStorage on mount
  useEffect(() => {
    const storedCode = localStorage.getItem(REFERRAL_CODE_KEY);
    if (storedCode) {
      setRefCode(storedCode);
    }
  }, []);

  // Set referral code
  const setReferralCode = useCallback((code: string) => {
    try {
      localStorage.setItem(REFERRAL_CODE_KEY, code);
      setRefCode(code);
    } catch (error) {
      console.error('Failed to store referral code:', error);
    }
  }, []);

  // Clear referral code
  const clearReferralCode = useCallback(() => {
    try {
      localStorage.removeItem(REFERRAL_CODE_KEY);
      setRefCode(null);
    } catch (error) {
      console.error('Failed to clear referral code:', error);
    }
  }, []);

  /*
  Read the referral code off whichever link shape the customer opened.

  `/r/<code>` is what the backend mints now; `/referral?code=<code>` is the
  older form, still answered because links already sent live in people's chat
  history and nothing can recall them. The path is tried FIRST — a link
  carrying both is the new shape, and the query would be the stale half.

  Case is not normalised here. The link spells the code lower-cased, codes are
  stored upper-case, and the backend upper-cases on lookup; doing it again here
  would put a second copy of that rule somewhere it could drift.
  */
  const extractCodeFromUrl = useCallback(() => {
    const fromPath = /^\/r\/([^/?#]+)/.exec(window.location.pathname)?.[1];
    const codeFromUrl =
      (fromPath && decodeURIComponent(fromPath)) ||
      new URLSearchParams(window.location.search).get('code');

    if (codeFromUrl) {
      setReferralCode(codeFromUrl);

      // Clean the URL so a refresh or a back-navigation cannot re-apply it.
      const urlObj = new URL(window.location.href);
      urlObj.searchParams.delete('code');
      if (fromPath) urlObj.pathname = '/';
      window.history.replaceState({}, '', urlObj.toString());

      return codeFromUrl;
    }

    return null;
  }, [setReferralCode]);

  return {
    refCode,
    setReferralCode,
    clearReferralCode,
    extractCodeFromUrl
  };
};
